import React, { useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import EditorGrid from '../components/editor/EditorGrid'
import InteractiveEditorGrid from '../components/editor/InteractiveEditorGrid'
import EditorToolbar from '../components/editor/EditorToolbar'
import EntryList from '../components/editor/EntryList'
import CluesEditor from '../components/editor/CluesEditor'
import PublishModal from '../components/editor/PublishModal'
import useKeyboardNavigation from '../hooks/useKeyboardNavigation'
import { createEmptyGrid, copyGrid, calculateNumberedCells, getSymmetricCell, applyPattern, clearGrid } from '../utils/gridUtils'
import { puzzleApi } from '../utils/api'

const STEPS = ['setup', 'grid', 'clues', 'preview']

const styles = {
  stepIndicator: {
    display: 'flex',
    gap: '8px',
    marginBottom: '24px',
  },
  stepButton: {
    padding: '8px 16px',
    border: '2px solid var(--color-border)',
    background: '#fff',
    color: 'var(--color-primary)',
    fontWeight: '600',
    cursor: 'pointer',
  },
  stepButtonActive: {
    background: 'var(--color-primary)',
    color: '#fff',
  },
  stepButtonCompleted: {
    background: '#ddd',
  },
  section: {
    marginBottom: '24px',
  },
  label: {
    display: 'block',
    fontWeight: '600',
    marginBottom: '8px',
  },
  input: {
    padding: '8px 12px',
    border: '2px solid var(--color-border)',
    fontSize: '1rem',
    width: '100%',
    maxWidth: '300px',
  },
  textarea: {
    padding: '8px 12px',
    border: '2px solid var(--color-border)',
    fontSize: '1rem',
    width: '100%',
    maxWidth: '400px',
    minHeight: '100px',
    fontFamily: 'inherit',
  },
  btn: {
    padding: '12px 24px',
    border: '2px solid var(--color-border)',
    background: 'var(--color-primary)',
    color: '#fff',
    fontWeight: '600',
    cursor: 'pointer',
    fontSize: '1rem',
  },
  btnSecondary: {
    background: '#fff',
    color: 'var(--color-primary)',
  },
  gridArea: {
    display: 'flex',
    gap: '40px',
    flexWrap: 'wrap',
  },
  coreWords: {
    marginBottom: '16px',
    padding: '12px',
    background: 'var(--color-light)',
    border: '1px solid #ccc',
  },
  navButtons: {
    marginTop: '24px',
    display: 'flex',
    gap: '12px',
  },
  previewContainer: {
    display: 'flex',
    gap: '40px',
    flexWrap: 'wrap',
  },
  cluesPreview: {
    flex: '1',
    minWidth: '300px',
  },
  hint: {
    marginTop: '16px',
    fontSize: '0.85rem',
    color: 'var(--color-muted)',
  },
}

export default function CreatePuzzle() {
  const navigate = useNavigate()

  // Setup state
  const [theme, setTheme] = useState('')
  const [gridSize, setGridSize] = useState(15)
  const [coreWords, setCoreWords] = useState('')

  // Grid state
  const [grid, setGrid] = useState(null)
  const [selectedCell, setSelectedCell] = useState(null)
  const [direction, setDirection] = useState('across')
  const [symmetryEnabled, setSymmetryEnabled] = useState(true)

  // Clues state
  const [clues, setClues] = useState({ across: {}, down: {} })

  // Navigation
  const [currentStep, setCurrentStep] = useState('setup')

  // Publish state
  const [showPublishModal, setShowPublishModal] = useState(false)
  const [publishedUrl, setPublishedUrl] = useState('')
  const [puzzleId, setPuzzleId] = useState(null)

  // Initialize grid
  const initializeGrid = () => {
    setGrid(createEmptyGrid(gridSize))
    setSelectedCell({ row: 0, col: 0 })
    setCurrentStep('grid')
  }

  // Calculate numbered cells
  const numberedCells = useMemo(() => {
    return calculateNumberedCells(grid)
  }, [grid])

  // Toggle black cell with optional symmetry
  const toggleBlackWithSymmetry = useCallback((row, col) => {
    setGrid(g => {
      const newGrid = copyGrid(g)
      const isBecomingBlack = !newGrid[row][col].isBlack

      newGrid[row][col].isBlack = isBecomingBlack
      if (isBecomingBlack) {
        newGrid[row][col].letter = ''
      }

      // Apply symmetric toggle if symmetry is enabled
      if (symmetryEnabled) {
        const sym = getSymmetricCell(row, col, g.length)
        // Don't toggle center cell twice
        if (sym.row !== row || sym.col !== col) {
          newGrid[sym.row][sym.col].isBlack = isBecomingBlack
          if (isBecomingBlack) {
            newGrid[sym.row][sym.col].letter = ''
          }
        }
      }

      return newGrid
    })
  }, [symmetryEnabled])

  // Letter input handler
  const handleLetterInput = useCallback((row, col, letter) => {
    if (!grid || grid[row][col].isBlack) return
    setGrid(g => {
      const newGrid = copyGrid(g)
      newGrid[row][col].letter = letter.toUpperCase()
      return newGrid
    })
  }, [grid])

  // Delete letter handler
  const handleDelete = useCallback((row, col) => {
    if (!grid || grid[row][col].isBlack) return
    setGrid(g => {
      const newGrid = copyGrid(g)
      newGrid[row][col].letter = ''
      return newGrid
    })
  }, [grid])

  // Apply pattern to grid
  const handleApplyPattern = useCallback((patternKey) => {
    if (!grid) return
    setGrid(g => applyPattern(g, patternKey))
  }, [grid])

  // Clear entire grid
  const handleClearGrid = useCallback(() => {
    if (!grid) return
    setGrid(g => clearGrid(g))
  }, [grid])

  // Keyboard navigation hook
  const { handleKeyDown } = useKeyboardNavigation({
    grid,
    selectedCell,
    setSelectedCell,
    direction,
    setDirection,
    onLetterInput: handleLetterInput,
    onDelete: handleDelete,
    numberedCells,
    onToggleBlack: toggleBlackWithSymmetry,
    isEditorMode: true,
  })

  // Clue change handler
  const handleClueChange = (direction, num, value) => {
    setClues(c => ({
      ...c,
      [direction]: { ...c[direction], [num]: value }
    }))
  }

  // Publish handler
  const handlePublish = async (creatorName) => {
    try {
      const data = {
        theme,
        gridSize,
        grid: grid.map(row => row.map(cell => ({
          letter: cell.letter,
          isBlack: cell.isBlack
        }))),
        clues
      }

      let result
      if (puzzleId) {
        await puzzleApi.update(puzzleId, data)
        result = await puzzleApi.publish(puzzleId, creatorName)
      } else {
        result = await puzzleApi.create({ ...data, creatorName })
        setPuzzleId(result.id)
        result = await puzzleApi.publish(result.id, creatorName)
      }

      const url = `${window.location.origin}/puzzle/${result.id}`
      setPublishedUrl(url)
    } catch (error) {
      console.error('Publish failed:', error)
      alert('Failed to publish puzzle. Please try again.')
    }
  }

  const stepIndex = STEPS.indexOf(currentStep)

  // Render Setup Step
  const renderSetup = () => (
    <div>
      <h2>Create a New Puzzle</h2>

      <div style={styles.section}>
        <label style={styles.label}>Puzzle Theme</label>
        <input
          type="text"
          value={theme}
          onChange={e => setTheme(e.target.value)}
          placeholder="e.g., Movie Mashups"
          style={styles.input}
        />
      </div>

      <div style={styles.section}>
        <label style={styles.label}>Grid Size</label>
        <select
          value={gridSize}
          onChange={e => setGridSize(Number(e.target.value))}
          style={{ ...styles.input, maxWidth: '150px' }}
        >
          {[9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21].map(size => (
            <option key={size} value={size}>{size} × {size}</option>
          ))}
        </select>
      </div>

      <div style={styles.section}>
        <label style={styles.label}>Core Theme Words (one per line)</label>
        <textarea
          value={coreWords}
          onChange={e => setCoreWords(e.target.value)}
          placeholder="Enter your theme answers here..."
          style={styles.textarea}
        />
        <p style={styles.hint}>
          These are your key answers that the puzzle will be built around.
        </p>
      </div>

      <button style={styles.btn} onClick={initializeGrid}>
        Start Building Grid →
      </button>
    </div>
  )

  // Render Grid Editor Step
  const renderGridEditor = () => (
    <div>
      <EditorToolbar
        direction={direction}
        symmetryEnabled={symmetryEnabled}
        setSymmetryEnabled={setSymmetryEnabled}
        gridSize={gridSize}
        onApplyPattern={handleApplyPattern}
        onClearGrid={handleClearGrid}
      />

      {coreWords && (
        <div style={styles.coreWords}>
          <strong>Core words to place:</strong>{' '}
          {coreWords.split('\n').filter(w => w.trim()).map((w, i) => (
            <span key={i} style={{ fontFamily: 'var(--font-mono)', marginRight: '12px' }}>
              {w.trim().toUpperCase()}
            </span>
          ))}
        </div>
      )}

      <div style={styles.gridArea}>
        <div>
          <InteractiveEditorGrid
            grid={grid}
            numberedCells={numberedCells}
            selectedCell={selectedCell}
            setSelectedCell={setSelectedCell}
            direction={direction}
            setDirection={setDirection}
            onKeyDown={handleKeyDown}
          />
          <p style={styles.hint}>
            Type letters directly • Space to toggle direction • Period (.) to toggle black squares • Arrow keys to navigate
          </p>
        </div>

        <EntryList
          acrossEntries={numberedCells.acrossEntries}
          downEntries={numberedCells.downEntries}
        />
      </div>

      <div style={styles.navButtons}>
        <button
          style={{ ...styles.btn, ...styles.btnSecondary }}
          onClick={() => setCurrentStep('setup')}
        >
          ← Back to Setup
        </button>
        <button style={styles.btn} onClick={() => setCurrentStep('clues')}>
          Edit Clues →
        </button>
      </div>
    </div>
  )

  // Render Clues Editor Step
  const renderCluesEditor = () => (
    <div>
      <h2>Write Your Clues</h2>
      <p style={{ marginBottom: '16px' }}>
        Enter clues for each entry. The word/pattern is shown for reference.
      </p>

      <CluesEditor
        acrossEntries={numberedCells.acrossEntries}
        downEntries={numberedCells.downEntries}
        clues={clues}
        onClueChange={handleClueChange}
      />

      <div style={styles.navButtons}>
        <button
          style={{ ...styles.btn, ...styles.btnSecondary }}
          onClick={() => setCurrentStep('grid')}
        >
          ← Back to Grid
        </button>
        <button style={styles.btn} onClick={() => setCurrentStep('preview')}>
          Preview & Publish →
        </button>
      </div>
    </div>
  )

  // Render Preview Step
  const renderPreview = () => (
    <div>
      <h2>{theme || 'Untitled Crossword'}</h2>

      <div style={styles.previewContainer}>
        <div>
          <h3>Grid Preview</h3>
          <EditorGrid
            grid={grid}
            numberedCells={numberedCells}
            selectedCell={null}
            onCellClick={() => {}}
            showLetters={false}
          />
        </div>

        <div style={styles.cluesPreview}>
          <h3>Clues</h3>
          <div style={{ display: 'flex', gap: '24px' }}>
            <div>
              <h4>Across</h4>
              {numberedCells.acrossEntries.map(e => (
                <div key={e.num} style={{ fontSize: '0.9rem', marginBottom: '4px' }}>
                  <strong>{e.num}.</strong>{' '}
                  {clues.across[e.num] || <em style={{ color: 'var(--color-muted)' }}>(no clue)</em>}
                </div>
              ))}
            </div>
            <div>
              <h4>Down</h4>
              {numberedCells.downEntries.map(e => (
                <div key={e.num} style={{ fontSize: '0.9rem', marginBottom: '4px' }}>
                  <strong>{e.num}.</strong>{' '}
                  {clues.down[e.num] || <em style={{ color: 'var(--color-muted)' }}>(no clue)</em>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div style={styles.navButtons}>
        <button
          style={{ ...styles.btn, ...styles.btnSecondary }}
          onClick={() => setCurrentStep('clues')}
        >
          ← Back to Clues
        </button>
        <button style={styles.btn} onClick={() => setShowPublishModal(true)}>
          Publish Puzzle
        </button>
      </div>

      {showPublishModal && (
        <PublishModal
          onClose={() => {
            setShowPublishModal(false)
            if (publishedUrl) {
              navigate(`/puzzle/${puzzleId}`)
            }
          }}
          onPublish={handlePublish}
          publishedUrl={publishedUrl}
        />
      )}
    </div>
  )

  return (
    <div>
      <div style={styles.stepIndicator}>
        {STEPS.map((step, idx) => (
          <button
            key={step}
            style={{
              ...styles.stepButton,
              ...(currentStep === step ? styles.stepButtonActive : {}),
              ...(idx < stepIndex ? styles.stepButtonCompleted : {}),
            }}
            onClick={() => {
              if (idx < stepIndex || (idx === stepIndex + 1 && grid)) {
                setCurrentStep(step)
              }
            }}
            disabled={idx > stepIndex + 1 || (idx > 0 && !grid)}
          >
            {idx + 1}. {step.charAt(0).toUpperCase() + step.slice(1)}
          </button>
        ))}
      </div>

      {currentStep === 'setup' && renderSetup()}
      {currentStep === 'grid' && grid && renderGridEditor()}
      {currentStep === 'clues' && grid && renderCluesEditor()}
      {currentStep === 'preview' && grid && renderPreview()}
    </div>
  )
}
